import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { safePrintHTML } from '@/utils/safePrint';
import { 
  TrendingUp, Archive, Users, Printer, Target, Trash2, Calendar, Filter, RefreshCw, Plus, X 
} from 'lucide-react';
import { api } from '@/utils/api';
import ObjectivesManagement from './ObjectivesManagement';

const API_BASE = '/cutting-simulator';

// Função para formatar minutos em HH:MM:SS ou MM:SS
const formatTime = (totalMinutes) => {
  if (totalMinutes === 0 || isNaN(totalMinutes)) return '0:00';
  
  const isNegative = totalMinutes < 0;
  const absMinutes = Math.abs(totalMinutes);
  
  const hours = Math.floor(absMinutes / 60);
  const mins = Math.floor(absMinutes % 60);
  const secs = Math.round((absMinutes % 1) * 60);
  
  const sign = isNegative ? '-' : '';
  
  if (hours > 0) {
    return `${sign}${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${sign}${mins}:${secs.toString().padStart(2, '0')}`;
};

// Função para formatar minutos em formato curto (ex: "5h 4min" ou "3min 20s")
const formatTimeShort = (totalMinutes) => {
  if (totalMinutes === 0 || isNaN(totalMinutes)) return '0min';
  
  const isNegative = totalMinutes < 0;
  const absMinutes = Math.abs(totalMinutes);
  
  const hours = Math.floor(absMinutes / 60);
  const mins = Math.floor(absMinutes % 60);
  const secs = Math.round((absMinutes % 1) * 60);
  
  const sign = isNegative ? '-' : '';
  
  if (hours > 0 && mins > 0) {
    return `${sign}${hours}h ${mins}min`;
  } else if (hours > 0) {
    return `${sign}${hours}h`;
  } else if (mins > 0 && secs > 0) {
    return `${sign}${mins}min ${secs}s`;
  } else if (mins > 0) {
    return `${sign}${mins}min`;
  }
  return `${sign}${secs}s`;
};

function Simulator({ 
  summary: globalSummary, 
  settings, 
  simEntreCortes, 
  setSimEntreCortes, 
  simulatedMetrics, 
  t,
  // Goals management props
  goals,
  loadingGoals,
  onCreateGoal,
  onToggleActive,
  onDeleteGoal,
  onRefreshGoal,
  onEditGoal,
  onGeneratePDF,
  selectedGoalForChart,
  setSelectedGoalForChart
}) {
  const [sampleStartDate, setSampleStartDate] = useState('');
  const [sampleEndDate, setSampleEndDate] = useState('');
  const [filteredSummary, setFilteredSummary] = useState(null);
  const [loadingFiltered, setLoadingFiltered] = useState(false);
  const [useFilteredData, setUseFilteredData] = useState(false);
  
  // Use filtered summary if available, otherwise use global
  const summary = (useFilteredData && filteredSummary) ? filteredSummary : globalSummary;
  
  const [simMaqParadaPct, setSimMaqParadaPct] = useState(() => {
    return 50;
  });
  const [simVelocidade, setSimVelocidade] = useState(0); // 0 means use actual
  const [archivedGoals, setArchivedGoals] = useState(() => {
    try {
      const saved = localStorage.getItem("cutting-archived-goals");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [showArchive, setShowArchive] = useState(false);
  const [simSubTab, setSimSubTab] = useState('simulator'); // 'simulator' or 'archive'

  // Fetch filtered summary when dates change
  const fetchFilteredSummary = useCallback(async () => {
    if (!sampleStartDate || !sampleEndDate) {
      setFilteredSummary(null);
      setUseFilteredData(false);
      return;
    }
    
    setLoadingFiltered(true);
    try {
      const params = new URLSearchParams();
      params.append('start_date', sampleStartDate);
      params.append('end_date', sampleEndDate);
      const res = await api.get(`${API_BASE}/summary?${params.toString()}`);
      setFilteredSummary(res.data);
      setUseFilteredData(true);
      
      // Keep the objective as is - don't change when filter changes
      // The objective is what the user wants to achieve, not the current value
      
      toast.success(`Dados filtrados: ${res.data.total_days} dia(s), ${res.data.total_cuts} cortes`);
    } catch (e) {
      toast.error('Erro ao filtrar dados');
      setFilteredSummary(null);
      setUseFilteredData(false);
    } finally {
      setLoadingFiltered(false);
    }
  }, [sampleStartDate, sampleEndDate]);

  // Clear filter and use all data
  const clearFilter = () => {
    setSampleStartDate('');
    setSampleEndDate('');
    setFilteredSummary(null);
    setUseFilteredData(false);
    
    // Keep the objective as is when clearing filter
    // Reset simEntreCortes if needed
    
    toast.info('A usar todos os dados');
  };

  if (!summary) return null;

  // Calculate time utility based on settings
  const parseTime = (timeStr) => {
    const [h, m] = (timeStr || "00:00").split(':').map(Number);
    return h * 60 + m;
  };
  const morningMin = parseTime(settings.morningEnd) - parseTime(settings.morningStart);
  const afternoonMin = parseTime(settings.afternoonEnd) - parseTime(settings.afternoonStart);
  const breaksMin = (settings.breakDuration || 10) * 2;
  const totalUsefulMin = morningMin + afternoonMin - breaksMin;

  // Current real values from data - use ADJUSTED values from daily_summary
  // Calculate ADJUSTED totals by summing from daily_summary (which has deductions applied)
  const dailySummary = summary.daily_summary || [];
  const adjustedCuttingTime = dailySummary.length > 0 
    ? dailySummary.reduce((sum, day) => sum + (day.total_cutting_time || 0), 0)
    : summary.total_cutting_time_min || 0;
  const adjustedMaqParada = dailySummary.length > 0
    ? dailySummary.reduce((sum, day) => sum + (day.total_machine_stopped || 0), 0)
    : summary.total_machine_stopped_min || 0;
  const adjustedEntreCortes = dailySummary.length > 0
    ? dailySummary.reduce((sum, day) => sum + (day.total_entre_cortes || 0), 0)
    : summary.total_entre_cortes_min || 0;
  const totalCuts = summary.total_cuts || 1;
  
  // Calculate REAL average entre cortes from actual data (not the incorrect summary value)
  const currentEntreCortes = adjustedEntreCortes / totalCuts;
  
  const currentCuttingTime = adjustedCuttingTime;
  const currentMaqParada = adjustedMaqParada;
  const totalCurrentEntreCortesMins = adjustedEntreCortes;
  
  // Calculate % machine stopped as ratio to CUTTING TIME (time the machine works)
  // Example: 50% means parada = 50% of cutting time (half the work time is idle)
  const currentMaqParadaPct = currentCuttingTime > 0 ? Math.round((currentMaqParada / currentCuttingTime) * 100 * 10) / 10 : 50;
  const totalDays = summary.total_days || 1;

  // SIMULATION CALCULATIONS
  // ==============================================
  // LÓGICA: O tempo poupado em "entre marcadas" e "máquina parada" 
  // torna-se TEMPO DISPONÍVEL EXTRA que pode ser usado para mais produção.
  // ==============================================
  
  // 1. Tempo poupado em "Entre Marcadas"
  // Actual: currentEntreCortes min/corte × total_cortes = totalCurrentEntreCortesMins
  // Novo: simEntreCortes min/corte × total_cortes = totalNewEntreCortes
  const totalCurrentEntreCortes = totalCurrentEntreCortesMins;
  const isEntreCortesSame = Math.abs(simEntreCortes - currentEntreCortes) < 0.05;
  const totalNewEntreCortes = isEntreCortesSame 
    ? totalCurrentEntreCortes 
    : simEntreCortes * summary.total_cuts;
  const timeSavedEntreCortes = Math.max(0, totalCurrentEntreCortes - totalNewEntreCortes);

  // 2. Tempo poupado em "Máquina Parada"
  // Actual: currentMaqParadaPct% do tempo de corte = currentMaqParada
  // Novo: simMaqParadaPct% do tempo de corte = newMaqParadaMin
  const isMaqParadaSame = Math.abs(simMaqParadaPct - currentMaqParadaPct) < 1;
  const newMaqParadaMin = isMaqParadaSame 
    ? currentMaqParada 
    : (currentCuttingTime * simMaqParadaPct) / 100;
  const timeSavedMaqParada = Math.max(0, currentMaqParada - newMaqParadaMin);

  // 3. TEMPO DISPONÍVEL EXTRA = soma das poupanças
  const tempoDisponivelExtra = timeSavedEntreCortes + timeSavedMaqParada;
  const tempoDisponivelExtraPerDay = tempoDisponivelExtra / totalDays;
  
  // 4. Como esse tempo extra se distribui (mantendo proporções SIMULADAS)
  // Proporções simuladas: com os novos tempos de parada e entre marcadas
  const simTotalBeforeExtra = currentCuttingTime + newMaqParadaMin + totalNewEntreCortes;
  const pctCorteSim = simTotalBeforeExtra > 0 ? currentCuttingTime / simTotalBeforeExtra : 0.5;
  const pctParadaSim = simTotalBeforeExtra > 0 ? newMaqParadaMin / simTotalBeforeExtra : 0.3;
  const pctEntreSim = simTotalBeforeExtra > 0 ? totalNewEntreCortes / simTotalBeforeExtra : 0.2;
  
  // Com o tempo extra, mantendo proporções simuladas, teríamos:
  const extraCorte = tempoDisponivelExtra * pctCorteSim;
  const extraParada = tempoDisponivelExtra * pctParadaSim;
  const extraEntre = tempoDisponivelExtra * pctEntreSim;
  
  // 5. Novos totais simulados
  const newCuttingTime = currentCuttingTime + extraCorte;
  const newTotalMaqParada = newMaqParadaMin + extraParada;
  const newTotalEntreCortes = totalNewEntreCortes + extraEntre;
  
  // 6. Eficiência = Corte / (Corte + Máq.Parada) — relação direta corte vs intra-marcadas
  const totalActual = currentCuttingTime + currentMaqParada + totalCurrentEntreCortes;
  const newTotalTime = newCuttingTime + newTotalMaqParada + newTotalEntreCortes;
  const currentEfficiency = (currentCuttingTime + currentMaqParada) > 0 
    ? (currentCuttingTime / (currentCuttingTime + currentMaqParada) * 100) : 0;
  const newEfficiency = (newCuttingTime + newTotalMaqParada) > 0 
    ? (newCuttingTime / (newCuttingTime + newTotalMaqParada) * 100) : 0;
  const efficiencyGain = newEfficiency - currentEfficiency;

  // 7. Estimativa de cortes extra
  const avgCuttingTimePerCut = summary.total_cuts > 0 ? (currentCuttingTime / summary.total_cuts) : 1;
  const estimatedExtraCuts = Math.round(tempoDisponivelExtra * pctCorteSim / avgCuttingTimePerCut);
  const newTotalCuts = summary.total_cuts + estimatedExtraCuts;
  const cutsIncreasePct = summary.total_cuts > 0 ? ((estimatedExtraCuts / summary.total_cuts) * 100) : 0;

  // Legacy variables for compatibility
  const totalTimeSaved = tempoDisponivelExtra;
  const totalTimeSavedPerDay = tempoDisponivelExtraPerDay;
  const totalTimeSavedHours = tempoDisponivelExtra / 60;
  const dailyGainMin = tempoDisponivelExtraPerDay;
  const dailyGainFormatted = dailyGainMin >= 60 
    ? `${Math.floor(dailyGainMin / 60)}h${Math.round(dailyGainMin % 60)}min`
    : `${Math.round(dailyGainMin)}min`;

  // 8. Additional metrics for the simulator table
  const currentTotalEntreMarcadas = totalCurrentEntreCortesMins;
  const currentTempoMaquina = summary.total_execution_time_min || 0;
  const currentTempoRealTotal = summary.total_real_time_min || 0;
  const currentPctEntreMarcadas = totalActual > 0 ? (totalCurrentEntreCortesMins / totalActual * 100) : 0;
  const currentPctTempoMaquina = currentTempoRealTotal > 0 ? ((currentTempoMaquina / currentTempoRealTotal) * 100) : 0;
  const currentRacioParagem = currentCuttingTime > 0 ? Math.round((currentMaqParada / currentCuttingTime) * 100) : 0;

  // 9. New simulated values
  const newTotalEntreMarcadas = totalNewEntreCortes;
  // When no changes, tempo máquina should stay the same
  const newTempoMaquina = (isEntreCortesSame && isMaqParadaSame) 
    ? currentTempoMaquina 
    : newCuttingTime + newMaqParadaMin; // Cutting + stopped (but NOT between markers)
  const newTempoRealTotal = currentTempoRealTotal; // SAME total time (work day is fixed)
  const newPctEntreMarcadas = newTempoRealTotal > 0 ? ((newTotalEntreMarcadas / newTempoRealTotal) * 100) : 0;
  const newPctTempoMaquina = newTempoRealTotal > 0 ? ((newTempoMaquina / newTempoRealTotal) * 100) : 0;
  // Ratio of stopped/cutting is recalculated with NEW cutting time
  const newRacioParagem = (isEntreCortesSame && isMaqParadaSame) 
    ? currentRacioParagem 
    : (newCuttingTime > 0 ? Math.round((newMaqParadaMin / newCuttingTime) * 100) : 0);

  // 11. Hours recovered (extra cutting capacity)
  const horasRecuperadas = totalTimeSavedHours;
  const diasEquivalentes8h = horasRecuperadas / 8;
  
  // Extra capacity percentage
  const extraCapacityPct = totalActual > 0 ? (totalTimeSaved / totalActual * 100) : 0;

  // Export goals for operators - Single page PDF
  const exportGoalsForOperators = () => {
    const goalData = {
      id: Date.now(),
      date: new Date().toISOString(),
      dateRange: sampleStartDate && sampleEndDate ? `${sampleStartDate} a ${sampleEndDate}` : 'Todos os dados',
      efficiency: settings.efficiencyTarget,
      entreCortes: settings.entreCorteTarget,
      speed: settings.speedTarget,
      maqParadaPct: simMaqParadaPct,
      workTime: `${Math.floor(totalUsefulMin / 60)}h${totalUsefulMin % 60 > 0 ? totalUsefulMin % 60 + 'min' : ''}`,
      breaks: `${settings.break1Start} e ${settings.break2Start}`,
      currentEfficiency: summary.efficiency,
      simulatedEfficiency: newEfficiency.toFixed(1),
      potentialGain: efficiencyGain.toFixed(1),
      dailyGain: dailyGainFormatted
    };

    // Save to archive
    const newArchive = [goalData, ...archivedGoals.slice(0, 19)];
    setArchivedGoals(newArchive);
    localStorage.setItem("cutting-archived-goals", JSON.stringify(newArchive));

    // Generate printable HTML - Single page
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Metas de Produção - ${new Date().toLocaleDateString('pt-PT')}</title>
        <style>
          @page { size: A4; margin: 15mm; }
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { 
            font-family: 'Segoe UI', system-ui, sans-serif; 
            padding: 0;
            background: white;
            font-size: 12px;
          }
          .container { max-width: 100%; }
          .header { 
            background: linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%);
            color: white; 
            padding: 16px 20px; 
            border-radius: 10px;
            text-align: center;
            margin-bottom: 16px;
          }
          .header h1 { font-size: 20px; margin-bottom: 4px; }
          .header p { opacity: 0.9; font-size: 11px; }
          .section { margin-bottom: 14px; }
          .section-title { 
            font-size: 11px; 
            text-transform: uppercase; 
            letter-spacing: 0.5px; 
            color: #64748b; 
            margin-bottom: 8px;
            font-weight: 600;
            border-bottom: 2px solid #8b5cf6;
            padding-bottom: 4px;
          }
          .goal-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
          .goal-card { 
            background: #f8fafc; 
            padding: 12px; 
            border-radius: 8px; 
            text-align: center;
            border: 1px solid #e2e8f0;
          }
          .goal-card .label { font-size: 9px; color: #64748b; margin-bottom: 2px; text-transform: uppercase; }
          .goal-card .value { font-size: 22px; font-weight: 700; color: #8b5cf6; }
          .goal-card .unit { font-size: 11px; color: #64748b; }
          .highlight { 
            background: linear-gradient(135deg, #10b981 0%, #059669 100%);
            color: white;
            padding: 14px;
            border-radius: 10px;
            text-align: center;
            margin: 14px 0;
          }
          .highlight .big { font-size: 32px; font-weight: 800; }
          .highlight .desc { font-size: 11px; opacity: 0.9; margin-top: 4px; }
          .info-row { 
            display: flex; 
            justify-content: space-between; 
            padding: 8px 12px;
            background: #f8fafc;
            border-radius: 6px;
            margin-bottom: 6px;
          }
          .info-row .label { color: #64748b; font-size: 11px; }
          .info-row .value { font-weight: 600; color: #1e293b; }
          .footer { 
            text-align: center; 
            margin-top: 14px; 
            padding-top: 10px;
            border-top: 1px solid #e2e8f0;
            color: #94a3b8; 
            font-size: 10px; 
          }
          @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1>Metas de Produção - Corte</h1>
            <p>Gerado: ${new Date().toLocaleDateString('pt-PT')} às ${new Date().toLocaleTimeString('pt-PT')} | Período: ${sampleStartDate && sampleEndDate ? `${sampleStartDate} a ${sampleEndDate}` : 'Todos os dados'} | ${totalDays} dias</p>
          </div>
          
          <!-- Objectivos -->
          <div class="section">
            <div class="section-title">Objectivos a Atingir</div>
            <div class="goal-grid">
              <div class="goal-card">
                <div class="label">Eficiência Mínima</div>
                <div class="value">${settings.efficiencyTarget}<span class="unit">%</span></div>
              </div>
              <div class="goal-card">
                <div class="label">Tempo Entre Cortes</div>
                <div class="value">≤${settings.entreCorteTarget}<span class="unit">min</span></div>
              </div>
              <div class="goal-card">
                <div class="label">Velocidade Média</div>
                <div class="value">≥${settings.speedTarget}<span class="unit">m/min</span></div>
              </div>
            </div>
          </div>
          
          <!-- Highlight -->
          <div class="highlight">
            <div class="big">${dailyGainFormatted}</div>
            <div class="desc">Ganho potencial por dia ao atingir estas metas</div>
          </div>
          
          <!-- Comparação -->
          <div class="section">
            <div class="section-title">Comparação de Eficiência</div>
            <div class="goal-grid">
              <div class="goal-card">
                <div class="label">Eficiência Actual</div>
                <div class="value" style="color:#ef4444">${summary.efficiency}<span class="unit">%</span></div>
              </div>
              <div class="goal-card">
                <div class="label">Eficiência Simulada</div>
                <div class="value" style="color:#10b981">${newEfficiency.toFixed(1)}<span class="unit">%</span></div>
              </div>
              <div class="goal-card">
                <div class="label">Ganho Potencial</div>
                <div class="value" style="color:#8b5cf6">+${efficiencyGain.toFixed(1)}<span class="unit">pp</span></div>
              </div>
            </div>
          </div>
          
          <!-- Detalhes da Simulação -->
          <div class="section">
            <div class="section-title">Parâmetros da Simulação</div>
            <div class="info-row">
              <span class="label">Tempo Entre Marcadas (simulado)</span>
              <span class="value">${simEntreCortes.toFixed(1)} min</span>
            </div>
            <div class="info-row">
              <span class="label">% Máquina Parada (simulado)</span>
              <span class="value">${simMaqParadaPct}%</span>
            </div>
            <div class="info-row">
              <span class="label">Cortes Adicionais Estimados</span>
              <span class="value" style="color:#10b981">+${estimatedExtraCuts?.toLocaleString() || 0}</span>
            </div>
          </div>
          
          <!-- Horário -->
          <div class="section">
            <div class="section-title">Horário de Trabalho</div>
            <div class="info-row">
              <span class="label">Manhã</span>
              <span class="value">${settings.morningStart} - ${settings.morningEnd}</span>
            </div>
            <div class="info-row">
              <span class="label">Tarde</span>
              <span class="value">${settings.afternoonStart} - ${settings.afternoonEnd}</span>
            </div>
            <div class="info-row">
              <span class="label">Pausas</span>
              <span class="value">${settings.break1Start} e ${settings.break2Start} (${settings.breakDuration}min cada)</span>
            </div>
          </div>
          
          <div class="footer">
            SAMIDEL - Sistema de Gestão Têxtil
          </div>
        </div>
      </body>
      </html>
    `;
    if (!safePrintHTML(printContent)) {
      toast.error('Popup bloqueado. Permita popups para exportar.');
    }
  };

  return (
    <div className="space-y-6" data-testid="simulator">
      {/* Sub-tabs for simulator sections */}
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={() => setSimSubTab('simulator')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            simSubTab === 'simulator' 
              ? 'bg-sky-500 text-white' 
              : `${t.inner} ${t.textSec} hover:opacity-80`
          }`}
        >
          Simulador
        </button>
        <button
          onClick={() => setSimSubTab('goals')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
            simSubTab === 'goals' 
              ? 'bg-violet-500 text-white' 
              : `${t.inner} ${t.textSec} hover:opacity-80`
          }`}
        >
          <Target className="w-4 h-4" />
          Objetivos
        </button>
        <button
          onClick={() => setSimSubTab('archive')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
            simSubTab === 'archive' 
              ? 'bg-sky-500 text-white' 
              : `${t.inner} ${t.textSec} hover:opacity-80`
          }`}
        >
          <Archive className="w-4 h-4" />
          Arquivo ({archivedGoals.length})
        </button>
      </div>

      {/* Objectives Management View */}
      {simSubTab === 'goals' && (
        <ObjectivesManagement t={t} />
      )}

      {/* Archive View */}
      {simSubTab === 'archive' && (
        <div className={`${t.card} rounded-lg p-6`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-lg font-semibold ${t.text}`}>
              Simulações Guardadas
            </h3>
            {archivedGoals.length > 0 && (
              <button
                onClick={() => {
                  if (window.confirm('Apagar todo o arquivo?')) {
                    setArchivedGoals([]);
                    localStorage.removeItem('cutting-archived-goals');
                    toast.success('Arquivo limpo');
                  }
                }}
                className="text-xs text-red-500 hover:text-red-400"
              >
                Limpar Arquivo
              </button>
            )}
          </div>
          
          {archivedGoals.length === 0 ? (
            <div className={`text-center py-12 ${t.textMut}`}>
              <Archive className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Nenhuma simulação guardada</p>
              <p className="text-sm mt-1">Guarde simulações para comparar depois</p>
            </div>
          ) : (
            <div className="space-y-3">
              {archivedGoals.map((goal, idx) => (
                <div key={goal.id || idx} className={`${t.inner} rounded-lg p-4`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className={`text-sm font-medium ${t.text}`}>
                        {new Date(goal.date).toLocaleDateString('pt-PT')} às {new Date(goal.date).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                      <div className={`text-xs ${t.textMut}`}>
                        {goal.dateRange || 'Todos os dados'}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-center">
                        <div className="text-xs text-gray-500">Actual</div>
                        <div className="text-lg font-bold text-red-500">{goal.currentEfficiency}%</div>
                      </div>
                      <div className="text-xl">→</div>
                      <div className="text-center">
                        <div className="text-xs text-gray-500">Simulado</div>
                        <div className="text-lg font-bold text-emerald-500">{goal.simulatedEfficiency}%</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-gray-500">Ganho/Dia</div>
                        <div className="text-lg font-bold text-sky-500">{goal.dailyGain}</div>
                      </div>
                      <button
                        onClick={() => {
                          const newArchive = archivedGoals.filter((_, i) => i !== idx);
                          setArchivedGoals(newArchive);
                          localStorage.setItem('cutting-archived-goals', JSON.stringify(newArchive));
                        }}
                        className="p-2 rounded hover:bg-red-500/10 text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Main Simulator */}
      {simSubTab === 'simulator' && (
      <div className={`${t.card} rounded-lg p-6`}>
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2.5 rounded-xl bg-violet-500/20">
            <TrendingUp className="w-6 h-6 text-violet-500" />
          </div>
          <div className="flex-1">
            <h2 className={`text-xl font-bold ${t.text}`} style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
              Simulador de Optimização
            </h2>
            <p className={`text-sm ${t.textMut}`}>
              Baseado em {summary.total_days} dias | {summary.total_cuts?.toLocaleString()} cortes
              {useFilteredData && <span className="ml-2 text-violet-500 font-medium">(filtrado)</span>}
            </p>
          </div>
        </div>

        {/* Impactful Date Range Filter */}
        <div className="mb-6 p-5 rounded-2xl bg-gradient-to-r from-violet-50 to-indigo-50 dark:from-violet-900/20 dark:to-indigo-900/20 border-2 border-violet-200 dark:border-violet-500/30">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-violet-500 text-white">
                <Calendar className="w-5 h-5" />
              </div>
              <div>
                <h3 className={`font-bold ${t.text}`}>Intervalo de Análise</h3>
                <p className={`text-xs ${t.textMut}`}>Defina o período que deseja simular</p>
              </div>
            </div>
            {useFilteredData && (
              <span className="px-3 py-1.5 rounded-full text-sm bg-violet-500 text-white font-semibold shadow-lg shadow-violet-200 dark:shadow-violet-900/30">
                ✓ Filtro Activo
              </span>
            )}
          </div>
          
          <div className="flex items-end gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <label className={`block text-sm font-semibold mb-2 ${t.text}`}>
                Data Início
              </label>
              <input
                type="date"
                value={sampleStartDate}
                onChange={(e) => setSampleStartDate(e.target.value)}
                className={`w-full px-4 py-3 rounded-xl text-base font-medium ${t.inputBg} ${t.card} ${t.text} border-2 border-violet-200 dark:border-violet-500/30 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 transition-all`}
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className={`block text-sm font-semibold mb-2 ${t.text}`}>
                Data Fim
              </label>
              <input
                type="date"
                value={sampleEndDate}
                onChange={(e) => setSampleEndDate(e.target.value)}
                className={`w-full px-4 py-3 rounded-xl text-base font-medium ${t.inputBg} ${t.card} ${t.text} border-2 border-violet-200 dark:border-violet-500/30 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 transition-all`}
              />
            </div>
            <button
              onClick={fetchFilteredSummary}
              disabled={!sampleStartDate || !sampleEndDate || loadingFiltered}
              className="flex items-center gap-2 px-6 py-3 rounded-xl text-base font-bold bg-gradient-to-r from-violet-500 to-indigo-500 text-white hover:from-violet-600 hover:to-indigo-600 shadow-lg shadow-violet-200 dark:shadow-violet-900/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
            >
              {loadingFiltered ? (
                <RefreshCw className="w-5 h-5 animate-spin" />
              ) : (
                <Filter className="w-5 h-5" />
              )}
              Aplicar
            </button>
            {useFilteredData && (
              <button
                onClick={clearFilter}
                className={`flex items-center gap-2 px-6 py-3 rounded-xl text-base font-semibold border-2 border-slate-300 dark:border-zinc-600 ${t.text} hover:bg-slate-100 dark:hover:bg-zinc-800 transition-all`}
              >
                <X className="w-5 h-5" />
                Limpar
              </button>
            )}
          </div>
          
          {useFilteredData && filteredSummary && (
            <div className="mt-4 pt-4 border-t border-violet-200 dark:border-violet-500/30">
              <div className="flex items-center gap-6 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className={`text-sm ${t.textMut}`}>Período:</span>
                  <span className="px-3 py-1 rounded-lg bg-white dark:bg-zinc-800 text-violet-600 dark:text-violet-400 font-bold shadow-sm">
                    {sampleStartDate?.split('-').reverse().join('/')} → {sampleEndDate?.split('-').reverse().join('/')}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-sm ${t.textMut}`}>Dias:</span>
                  <span className="px-3 py-1 rounded-lg bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 font-bold">
                    {filteredSummary.total_days}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-sm ${t.textMut}`}>Cortes:</span>
                  <span className="px-3 py-1 rounded-lg bg-sky-100 dark:bg-sky-500/20 text-sky-700 dark:text-sky-400 font-bold">
                    {filteredSummary.total_cuts?.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        <p className={`text-sm ${t.textMut} mb-6`}>
          Ajuste os objectivos abaixo para simular quanto tempo pode ganhar por dia, <strong>mantendo as pausas legais</strong> ({breaksMin}min de pausas + {Math.floor((parseTime(settings.afternoonStart) - parseTime(settings.morningEnd)) / 60)}h de almoço).
        </p>

        {/* Show sliders only when filter is applied */}
        {!useFilteredData && (
          <div className={`${t.inner} rounded-xl p-8 text-center`}>
            <Calendar className="w-12 h-12 mx-auto mb-4 text-violet-400 opacity-50" />
            <p className={`text-lg font-medium ${t.text} mb-2`}>Seleccione um período para simular</p>
            <p className={`text-sm ${t.textMut}`}>
              Defina as datas de início e fim acima e clique em "Aplicar" para ver os resultados da simulação.
            </p>
          </div>
        )}

        {useFilteredData && filteredSummary ? (
        <>
        {/* Two sliders side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Slider 1: Entre Marcadas */}
          <div className={`p-5 rounded-lg ${t.inner}`}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <span className={`text-sm font-medium ${t.text}`}>Tempo Entre Marcadas</span>
                <p className={`text-xs ${t.textMut}`}>Tempo de paragem entre operações de corte</p>
              </div>
              <input
                type="number"
                min="0.1"
                max="10"
                step="0.1"
                value={simEntreCortes}
                onChange={(e) => {
                  const val = parseFloat(e.target.value);
                  if (!isNaN(val) && val >= 0.1 && val <= 10) setSimEntreCortes(parseFloat(val.toFixed(1)));
                }}
                className={`w-24 text-2xl font-bold text-amber-500 text-right bg-transparent border-b-2 border-amber-500/30 focus:border-amber-500 outline-none`}
              />
              <span className="text-2xl font-bold text-amber-500 ml-1">min</span>
            </div>
            <div className="relative">
              <input
                type="range"
                min="0.1"
                max="10"
                step="0.1"
                value={simEntreCortes}
                onChange={(e) => setSimEntreCortes(parseFloat(parseFloat(e.target.value).toFixed(1)))}
                className="w-full h-3 rounded-lg appearance-none cursor-pointer"
                style={{ 
                  background: `linear-gradient(to right, #f59e0b 0%, #f59e0b ${((simEntreCortes - 0.1) / 9.9) * 100}%, ${t.key === 'dark' ? '#27272a' : '#e2e8f0'} ${((simEntreCortes - 0.1) / 9.9) * 100}%, ${t.key === 'dark' ? '#27272a' : '#e2e8f0'} 100%)`
                }}
              />
            </div>
            <div className={`flex justify-between text-xs ${t.textMut} mt-2`}>
              <span>0:06 (óptimo)</span>
              <span className="text-amber-500">Actual: {formatTime(currentEntreCortes)}</span>
              <span>10:00 (lento)</span>
            </div>
            {simEntreCortes < currentEntreCortes && (
              <div className="mt-3 p-2 rounded bg-emerald-500/10 text-emerald-600 text-xs flex items-center gap-2">
                <TrendingUp className="w-3 h-3" />
                <span>
                  Ganho de <strong>{formatTime(currentEntreCortes - simEntreCortes)}</strong> × {summary.total_cuts} cortes = <strong>+{formatTime(timeSavedEntreCortes)}</strong> de tempo extra
                </span>
              </div>
            )}
          </div>
          
          {/* Slider 2: % Máquina Parada - OBJETIVO */}
          <div className={`p-5 rounded-lg ${t.inner}`}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <span className={`text-sm font-medium ${t.text}`}>% Máquina Parada</span>
                <p className={`text-xs ${t.textMut}`}>Objetivo: parada como % do tempo de corte (ex: 50% = metade do tempo de corte)</p>
              </div>
              <div className="flex items-center">
                <input
                  type="number"
                  min="1"
                  max="200"
                  step="1"
                  value={simMaqParadaPct}
                  onChange={(e) => {
                    const val = parseInt(e.target.value);
                    if (!isNaN(val) && val >= 1 && val <= 200) setSimMaqParadaPct(val);
                  }}
                  className={`w-20 text-2xl font-bold text-red-500 text-right bg-transparent border-b-2 border-red-500/30 focus:border-red-500 outline-none`}
                />
                <span className="text-2xl font-bold text-red-500 ml-1">%</span>
              </div>
            </div>
            <div className="relative">
              <input
                type="range"
                min="1"
                max="200"
                step="1"
                value={Math.min(simMaqParadaPct, 200)}
                onChange={(e) => setSimMaqParadaPct(parseInt(e.target.value))}
                className="w-full h-3 rounded-lg appearance-none cursor-pointer"
                style={{ 
                  background: `linear-gradient(to right, #ef4444 0%, #ef4444 ${((Math.min(simMaqParadaPct, 200) - 1) / 199) * 100}%, ${t.key === 'dark' ? '#27272a' : '#e2e8f0'} ${((Math.min(simMaqParadaPct, 200) - 1) / 199) * 100}%, ${t.key === 'dark' ? '#27272a' : '#e2e8f0'} 100%)`
                }}
              />
            </div>
            <div className={`flex justify-between text-xs ${t.textMut} mt-2`}>
              <span>1% (óptimo)</span>
              <span className="text-red-500 font-medium">Actual: {currentMaqParadaPct}%</span>
              <span>200%</span>
            </div>
            {simMaqParadaPct < currentMaqParadaPct && (
              <div className="mt-3 p-2 rounded bg-emerald-500/10 text-emerald-600 text-xs flex items-center gap-2">
                <TrendingUp className="w-3 h-3" />
                Reduzir de {currentMaqParadaPct}% para {simMaqParadaPct}% = <strong>+{formatTime(timeSavedMaqParada)}</strong> ganho total
              </div>
            )}
          </div>

          {/* Slider 3: Velocidade de Corte */}
          <div className={`p-5 rounded-lg ${t.inner}`}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <span className={`text-sm font-medium ${t.text}`}>Velocidade de Corte</span>
                <p className={`text-xs ${t.textMut}`}>Simular impacto de alterar velocidade (m/min)</p>
              </div>
              <div className="flex items-center">
                <input
                  type="number"
                  min="1"
                  max="30"
                  step="0.1"
                  value={simVelocidade || Math.round((summary.weighted_avg_speed || summary.avg_speed || 5) * 10) / 10}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value);
                    if (!isNaN(val) && val >= 1 && val <= 30) setSimVelocidade(parseFloat(val.toFixed(1)));
                  }}
                  className={`w-20 text-2xl font-bold text-sky-500 text-right bg-transparent border-b-2 border-sky-500/30 focus:border-sky-500 outline-none`}
                />
                <span className="text-lg font-bold text-sky-500 ml-1">m/min</span>
              </div>
            </div>
            <div className="relative">
              <input
                type="range"
                min="1"
                max="30"
                step="0.1"
                value={simVelocidade || (summary.weighted_avg_speed || summary.avg_speed || 5)}
                onChange={(e) => setSimVelocidade(parseFloat(parseFloat(e.target.value).toFixed(1)))}
                className="w-full h-3 rounded-lg appearance-none cursor-pointer"
                style={{ 
                  background: `linear-gradient(to right, #0ea5e9 0%, #0ea5e9 ${(((simVelocidade || (summary.weighted_avg_speed || summary.avg_speed || 5)) - 1) / 29) * 100}%, ${t.key === 'dark' ? '#27272a' : '#e2e8f0'} ${(((simVelocidade || (summary.weighted_avg_speed || summary.avg_speed || 5)) - 1) / 29) * 100}%, ${t.key === 'dark' ? '#27272a' : '#e2e8f0'} 100%)`
                }}
              />
            </div>
            <div className={`flex justify-between text-xs ${t.textMut} mt-2`}>
              <span>1 m/min</span>
              <span className="text-sky-500 font-medium">Actual: {(summary.weighted_avg_speed || summary.avg_speed || 0).toFixed(1)} m/min</span>
              <span>30 m/min</span>
            </div>
          </div>
        </div>

        {/* RESULTADO DA SIMULAÇÃO - Novo layout claro */}
        <div className={`p-6 rounded-xl border-2 ${tempoDisponivelExtra > 0 ? 'border-emerald-500 bg-emerald-500/5' : 'border-gray-300 bg-gray-50 dark:bg-zinc-800/50 dark:border-zinc-700'}`}>
          <div className="text-center mb-6">
            <h4 className={`text-sm uppercase tracking-wider ${tempoDisponivelExtra > 0 ? 'text-emerald-600' : t.textMut} mb-2`}>
              Tempo Disponível Extra (Período: {totalDays} dias)
            </h4>
            <span className={`text-5xl font-black ${tempoDisponivelExtra > 0 ? 'text-emerald-500' : t.textMut}`}>
              +{formatTime(tempoDisponivelExtra)}
            </span>
            <p className={`text-sm ${t.textMut} mt-2`}>
              ({formatTimeShort(tempoDisponivelExtra)} no período | +{formatTime(tempoDisponivelExtraPerDay)}/dia)
            </p>
          </div>
          
          {/* Breakdown das poupanças */}
          {tempoDisponivelExtra > 0 && (
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className={`p-4 rounded-lg ${t.inner} text-center`}>
                <div className="text-xs text-amber-500 uppercase mb-1">Poupança Entre Marcadas</div>
                <div className="text-xl font-bold text-amber-400">+{formatTime(timeSavedEntreCortes)}</div>
                <div className={`text-xs ${t.textMut}`}>({formatTimeShort(timeSavedEntreCortes)})</div>
              </div>
              <div className={`p-4 rounded-lg ${t.inner} text-center`}>
                <div className="text-xs text-red-500 uppercase mb-1">Poupança Máq. Parada</div>
                <div className="text-xl font-bold text-red-400">+{formatTime(timeSavedMaqParada)}</div>
                <div className={`text-xs ${t.textMut}`}>({formatTimeShort(timeSavedMaqParada)})</div>
              </div>
            </div>
          )}
          
          {/* O que significa este tempo extra */}
          {tempoDisponivelExtra > 0 && (
            <div className={`${t.inner} rounded-lg p-4`}>
              <h5 className={`text-xs uppercase tracking-wider ${t.textMut} mb-3`}>
                Com este tempo extra, mantendo proporções actuais:
              </h5>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <div className="text-lg font-bold text-sky-400">+{formatTime(extraCorte)}</div>
                  <div className={`text-xs ${t.textMut}`}>Corte adicional</div>
                  <div className="text-xs text-emerald-400">≈ +{estimatedExtraCuts} cortes</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-red-400">+{formatTime(extraParada)}</div>
                  <div className={`text-xs ${t.textMut}`}>Parada adicional</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-amber-400">+{formatTime(extraEntre)}</div>
                  <div className={`text-xs ${t.textMut}`}>Entre marcadas</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Comparison Table - Clean layout */}
        <div className="overflow-x-auto mt-6">
          <table className={`w-full text-sm ${t.text}`}>
            <thead>
              <tr className={`${t.inner}`}>
                <th className="text-left py-3 px-4">INDICADOR</th>
                <th className="text-center py-3 px-4 text-red-400">ACTUAL</th>
                <th className="text-center py-3 px-4 text-emerald-500">SIMULADO</th>
                <th className="text-center py-3 px-4">DIFERENÇA</th>
              </tr>
            </thead>
            <tbody>
              {/* SECTION: Distribuição de Tempo */}
              <tr className={`${t.card}`}>
                <td colSpan={4} className="py-2 px-4 font-bold text-sky-500 uppercase text-xs tracking-wider">
                  Distribuição de Tempo
                </td>
              </tr>
              {(() => {
                const currentTotal = currentCuttingTime + currentMaqParada + totalCurrentEntreCortes;
                const newTotal = newCuttingTime + newTotalMaqParada + newTotalEntreCortes;
                const rows = [
                  { label: 'Tempo de Corte', current: currentCuttingTime, simulated: newCuttingTime, currentPct: currentTotal > 0 ? (currentCuttingTime / currentTotal * 100) : 0, simPct: newTotal > 0 ? (newCuttingTime / newTotal * 100) : 0 },
                  { label: 'Tempo Intra Marcadas', current: currentMaqParada, simulated: newTotalMaqParada, currentPct: currentTotal > 0 ? (currentMaqParada / currentTotal * 100) : 0, simPct: newTotal > 0 ? (newTotalMaqParada / newTotal * 100) : 0 },
                  { label: 'Tempo Entre Marcadas', current: totalCurrentEntreCortes, simulated: newTotalEntreCortes, currentPct: currentTotal > 0 ? (totalCurrentEntreCortes / currentTotal * 100) : 0, simPct: newTotal > 0 ? (newTotalEntreCortes / newTotal * 100) : 0 },
                ];
                return (
                  <>
                    {rows.map((r, i) => {
                      const diff = r.simulated - r.current;
                      return (
                        <tr key={r.label} className={`border-t ${t.divider}`}>
                          <td className="py-3 px-4">{r.label}</td>
                          <td className="py-3 px-4 text-center font-mono">{formatTime(r.current)} <span className={`text-xs ${t.textMut}`}>({r.currentPct.toFixed(1)}%)</span></td>
                          <td className="py-3 px-4 text-center font-mono">{formatTime(r.simulated)} <span className={`text-xs ${t.textMut}`}>({r.simPct.toFixed(1)}%)</span></td>
                          <td className={`py-3 px-4 text-center`}>
                            {Math.abs(diff) > 0.1 ? (
                              <span className={`px-2 py-1 rounded text-xs font-bold ${diff < 0 && i > 0 ? 'bg-emerald-500/20 text-emerald-400' : diff > 0 && i === 0 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                                {diff > 0 ? '+' : ''}{formatTime(diff)}
                              </span>
                            ) : (
                              <span className={`px-2 py-1 rounded text-xs font-bold ${t.inner} ${t.textMut}`}>Igual</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    <tr className={`border-t-2 ${t.divider} font-bold`}>
                      <td className="py-3 px-4 text-sky-500">Total</td>
                      <td className="py-3 px-4 text-center font-mono text-sky-500">{formatTime(currentTotal)}</td>
                      <td className="py-3 px-4 text-center font-mono text-sky-500">{formatTime(newTotal)}</td>
                      <td className="py-3 px-4 text-center">
                        <span className={`text-xs ${t.textMut}`}>100%</span>
                      </td>
                    </tr>
                  </>
                );
              })()}
              
              {/* SECTION: Métricas */}
              <tr className={`${t.card}`}>
                <td colSpan={4} className="py-2 px-4 font-bold text-violet-500 uppercase text-xs tracking-wider">
                  Métricas
                </td>
              </tr>
              <tr className={`border-t ${t.divider}`}>
                <td className="py-3 px-4">
                  <div>Eficiência (% a Cortar)</div>
                  <div className={`text-xs ${t.textMut}`}>Corte / (Corte + Intra Marc.)</div>
                </td>
                <td className="py-3 px-4 text-center">{currentEfficiency.toFixed(1)}%</td>
                <td className="py-3 px-4 text-center">{newEfficiency.toFixed(1)}%</td>
                <td className={`py-3 px-4 text-center`}>
                  {efficiencyGain > 0.1 ? (
                    <span className={`px-2 py-1 rounded text-xs font-bold bg-emerald-500/20 text-emerald-400`}>+{efficiencyGain.toFixed(1)}pp</span>
                  ) : (
                    <span className={`px-2 py-1 rounded text-xs font-bold ${t.inner} ${t.textMut}`}>Igual</span>
                  )}
                </td>
              </tr>
              <tr className={`border-t ${t.divider}`}>
                <td className="py-3 px-4">
                  <div>% Intra Marcadas / Corte</div>
                  <div className={`text-xs ${t.textMut}`}>Parada / Corte × 100</div>
                </td>
                <td className="py-3 px-4 text-center">{currentMaqParadaPct}%</td>
                <td className="py-3 px-4 text-center">{simMaqParadaPct}%</td>
                <td className={`py-3 px-4 text-center`}>
                  {!isMaqParadaSame ? (
                    <span className={`px-2 py-1 rounded text-xs font-bold bg-emerald-500/20 text-emerald-400`}>-{currentMaqParadaPct - simMaqParadaPct}pp</span>
                  ) : (
                    <span className={`px-2 py-1 rounded text-xs font-bold ${t.inner} ${t.textMut}`}>Igual</span>
                  )}
                </td>
              </tr>
              <tr className={`border-t ${t.divider}`}>
                <td className="py-3 px-4">
                  <div>Quantidade de Cortes</div>
                  <div className={`text-xs ${t.textMut}`}>Total no período</div>
                </td>
                <td className="py-3 px-4 text-center">{summary.total_cuts}</td>
                <td className="py-3 px-4 text-center">{newTotalCuts}</td>
                <td className={`py-3 px-4 text-center`}>
                  {estimatedExtraCuts > 0 ? (
                    <span className={`px-2 py-1 rounded text-xs font-bold bg-emerald-500/20 text-emerald-400`}>+{estimatedExtraCuts} ({cutsIncreasePct.toFixed(1)}%)</span>
                  ) : (
                    <span className={`px-2 py-1 rounded text-xs font-bold ${t.inner} ${t.textMut}`}>Igual</span>
                  )}
                </td>
              </tr>
              <tr className={`border-t ${t.divider}`}>
                <td className="py-3 px-4">
                  <div>Média Entre Marcadas</div>
                  <div className={`text-xs ${t.textMut}`}>Por corte</div>
                </td>
                <td className="py-3 px-4 text-center font-mono">{formatTime(currentEntreCortes)}</td>
                <td className="py-3 px-4 text-center font-mono">{formatTime(simEntreCortes)}</td>
                <td className={`py-3 px-4 text-center`}>
                  {!isEntreCortesSame && currentEntreCortes > simEntreCortes ? (
                    <span className={`px-2 py-1 rounded text-xs font-bold bg-emerald-500/20 text-emerald-400`}>-{formatTime(currentEntreCortes - simEntreCortes)}</span>
                  ) : (
                    <span className={`px-2 py-1 rounded text-xs font-bold ${t.inner} ${t.textMut}`}>Igual</span>
                  )}
                </td>
              </tr>
              <tr className={`border-t ${t.divider}`}>
                <td className="py-3 px-4">
                  <div>Velocidade Ponderada</div>
                  <div className={`text-xs ${t.textMut}`}>Vel. real de corte</div>
                </td>
                <td className="py-3 px-4 text-center">{(summary.weighted_avg_speed || summary.avg_speed || 0).toFixed(1)} m/min</td>
                <td className="py-3 px-4 text-center">{(simVelocidade || summary.weighted_avg_speed || summary.avg_speed || 0).toFixed(1)} m/min</td>
                <td className={`py-3 px-4 text-center`}>
                  {simVelocidade && Math.abs(simVelocidade - (summary.weighted_avg_speed || summary.avg_speed || 0)) > 0.1 ? (
                    <span className={`px-2 py-1 rounded text-xs font-bold ${simVelocidade > (summary.weighted_avg_speed || summary.avg_speed || 0) ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                      {simVelocidade > (summary.weighted_avg_speed || summary.avg_speed || 0) ? '+' : ''}{(simVelocidade - (summary.weighted_avg_speed || summary.avg_speed || 0)).toFixed(1)} m/min
                    </span>
                  ) : (
                    <span className={`px-2 py-1 rounded text-xs font-bold ${t.inner} ${t.textMut}`}>Igual</span>
                  )}
                </td>
              </tr>
              <tr className={`border-t ${t.divider}`}>
                <td className="py-3 px-4">
                  <div>Perímetro de Corte</div>
                  <div className={`text-xs ${t.textMut}`}>Total cortado</div>
                </td>
                <td className="py-3 px-4 text-center">{Math.round(summary.total_perimeter || 0).toLocaleString()} m</td>
                <td className="py-3 px-4 text-center">{Math.round((summary.total_perimeter || 0) * (newTotalCuts / Math.max(summary.total_cuts, 1))).toLocaleString()} m</td>
                <td className={`py-3 px-4 text-center`}>
                  {estimatedExtraCuts > 0 ? (
                    <span className={`px-2 py-1 rounded text-xs font-bold bg-emerald-500/20 text-emerald-400`}>
                      +{Math.round((summary.total_perimeter || 0) * (estimatedExtraCuts / Math.max(summary.total_cuts, 1))).toLocaleString()} m
                    </span>
                  ) : (
                    <span className={`px-2 py-1 rounded text-xs font-bold ${t.inner} ${t.textMut}`}>Igual</span>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Summary Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mt-6 p-4 rounded-lg bg-gradient-to-r from-emerald-500/10 to-sky-500/10 border border-emerald-500/20">
          <div className="text-center">
            <div className="text-xl font-bold text-emerald-400">+{Math.round(totalTimeSaved/60)}h</div>
            <div className={`text-[10px] ${t.textMut} uppercase`}>Horas Corte Extra</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold text-emerald-400">+{estimatedExtraCuts?.toLocaleString()}</div>
            <div className={`text-[10px] ${t.textMut} uppercase`}>Cortes Extra</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold text-emerald-400">{newEfficiency.toFixed(1)}%</div>
            <div className={`text-[10px] ${t.textMut} uppercase`}>Nova Eficiência</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold text-emerald-400">+{efficiencyGain.toFixed(1)}pp</div>
            <div className={`text-[10px] ${t.textMut} uppercase`}>Ganho Eficiência</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold text-emerald-400">{horasRecuperadas.toFixed(0)}h</div>
            <div className={`text-[10px] ${t.textMut} uppercase`}>Horas Recuperadas</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold text-emerald-400">{diasEquivalentes8h.toFixed(0)}</div>
            <div className={`text-[10px] ${t.textMut} uppercase`}>Dias 8H Equivalentes</div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-3 pt-6 border-t border-gray-200 dark:border-gray-700 mt-6">
          <button
            onClick={() => {
              // Save to archive with full data
              const goalData = {
                id: Date.now(),
                date: new Date().toISOString(),
                dateRange: sampleStartDate && sampleEndDate ? `${sampleStartDate} a ${sampleEndDate}` : 'Todos os dados',
                simEntreCortes: simEntreCortes,
                simMaqParadaPct: simMaqParadaPct,
                currentEfficiency: summary.efficiency,
                simulatedEfficiency: newEfficiency.toFixed(1),
                potentialGain: efficiencyGain.toFixed(1),
                dailyGain: dailyGainFormatted,
                horasRecuperadas: horasRecuperadas.toFixed(0),
                diasEquivalentes: diasEquivalentes8h.toFixed(0),
                extraCapacity: extraCapacityPct.toFixed(1),
                totalDays: totalDays,
              };
              const newArchive = [goalData, ...archivedGoals.slice(0, 19)];
              setArchivedGoals(newArchive);
              localStorage.setItem("cutting-archived-goals", JSON.stringify(newArchive));
              toast.success('Simulação guardada no arquivo!');
              setSimSubTab('archive');
            }}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-sky-500 text-white hover:bg-sky-600 transition-colors"
          >
            <Archive className="w-4 h-4" />
            Guardar no Arquivo
          </button>
          <button
            onClick={exportGoalsForOperators}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 transition-colors`}
          >
            <Users className="w-4 h-4" />
            Exportar PDF
          </button>
          <button
            onClick={exportGoalsForOperators}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg ${t.inner} ${t.textSec} hover:opacity-80 transition-opacity`}
          >
            <Printer className="w-4 h-4" />
            Imprimir
          </button>
          <button
            onClick={() => { setSimEntreCortes(currentEntreCortes); setSimMaqParadaPct(currentMaqParadaPct); }}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg ${t.inner} ${t.textMut} hover:opacity-80 transition-opacity`}
            title="Definir objectivos iguais aos valores actuais (sem melhoria)"
          >
            Usar Valores Actuais
          </button>
          <button
            onClick={() => { setSimEntreCortes(2); setSimMaqParadaPct(50); }}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20 transition-colors`}
            title="Repor objectivos padrão (2 min entre cortes, 50% parada)"
          >
            Objectivos Padrão
          </button>
        </div>
        </>
        ) : null}
      </div>
      )}
    </div>
  );
}

export default Simulator;
